import { useState } from "react";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import {
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import {
  TimelineOppositeContent,
  timelineOppositeContentClasses,
} from "@mui/lab";
import {
  AccessTime,
  LocationOn,
  Description,
  PersonOutline,
  PanTool,
  ChatBubbleOutline,
} from "@mui/icons-material";
import localize from "../../../common/localize";
import ModalBackground from "../../../components/common/ModalBackground";
import DynamicFormReview from "../../dynamicForm/components/review/DynamicFormReview";
import { getClearBasicModel } from "../../../common/EntitiesManagement";
import { getGoogleMapsLink } from "../../../common/utils";
import DynamicFormHeaderForReport from "./report/DynamicFormHeaderForReport";

const StatusChangeTimelineItem = ({
  setStatusChangeId,
  eventModel,
  timelineObj,
  isFirstOne,
  setDynamicFormModel,
  setShowDynamicFormModal,
  isForReport,
  isLast,
}) => {
  const status = isFirstOne
    ? timelineObj?.InitialStatus
    : timelineObj?.FinalStatus;
  const bgColor = status?.BackgroundColor || "#002121";
  const fgColor = status?.ForegroundColor || "#FFFFFF";
  const reportedBy = isFirstOne
    ? eventModel?.CreatedBy
    : timelineObj.CreatedBy;
  const changeDate = new Date(
    isFirstOne ? eventModel?.CreationDate : timelineObj?.ChangeDate,
  );
  const hasGps =
    !isFirstOne &&
    (timelineObj.GpsLattitude < 0 || timelineObj.GpsLattitude > 0) &&
    (timelineObj.GpsLongitude < 0 || timelineObj.GpsLongitude > 0);
  const hasDynamicForm =
    !isFirstOne && timelineObj?.DynamicForm && !isForReport;
  const hasObservation =
    !isFirstOne && timelineObj?.Observation?.length > 0;

  const handleDynamicFormClick = (dynamicForm) => {
    setStatusChangeId(timelineObj?.Id);
    if (setDynamicFormModel) {
      setDynamicFormModel(dynamicForm);
    }
    setShowDynamicFormModal(true);
  };

  return (
    <TimelineItem>
      <TimelineOppositeContent sx={{ flex: "0 0 140px", pt: 2.5, display: { xs: "none", sm: "block" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "flex-end" }}>
          <AccessTime sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography variant="caption" color="text.secondary" noWrap>
            {changeDate.toLocaleString(undefined, {
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", textAlign: "right", fontSize: 10 }}>
          {changeDate.toLocaleString(undefined, { year: "numeric" })}
        </Typography>
      </TimelineOppositeContent>

      <TimelineSeparator>
        <TimelineDot
          sx={{
            bgcolor: bgColor,
            boxShadow: `0 0 0 3px ${bgColor}33`,
            width: 14,
            height: 14,
            m: "12px 0",
          }}
        />
        {!isLast && <TimelineConnector sx={{ bgcolor: "divider" }} />}
      </TimelineSeparator>

      <TimelineContent sx={{ pb: 3, pt: 1 }}>
        <Box sx={{ display: { xs: "flex", sm: "none" }, alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <AccessTime sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography variant="caption" color="text.secondary">
            {changeDate.toLocaleString(undefined, {
              month: "short",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        </Box>
        <Box
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            transition: "box-shadow 0.2s ease",
            "&:hover": {
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: bgColor,
              color: fgColor,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {status?.Name}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {timelineObj.IsManual > 0 && (
                <Chip
                  icon={<PanTool sx={{ fontSize: 12, color: `${fgColor} !important` }} />}
                  label={localize.executed_manually}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: `${fgColor}22`,
                    color: fgColor,
                    border: `1px solid ${fgColor}44`,
                    "& .MuiChip-icon": { ml: 0.5 },
                  }}
                />
              )}

              {hasGps && (
                <Tooltip title="GPS" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const win = window.open(
                        getGoogleMapsLink(
                          timelineObj.GpsLattitude,
                          timelineObj.GpsLongitude,
                        ),
                        "_blank",
                      );
                      if (win) win.focus();
                    }}
                    sx={{ color: fgColor }}
                  >
                    <LocationOn sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Box sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: hasObservation ? 1 : 0 }}>
              <PersonOutline sx={{ fontSize: 14, color: "text.disabled" }} />
              <Typography variant="caption" color="text.secondary">
                {reportedBy}
              </Typography>
            </Box>

            {hasObservation && (
              <Box
                sx={{
                  mt: 0.5,
                  px: 1.5,
                  py: 1,
                  bgcolor: "#f8f9fa",
                  borderRadius: 1,
                  borderLeft: "3px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                  <ChatBubbleOutline sx={{ fontSize: 13, color: "text.disabled", mt: 0.25 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5 }}
                  >
                    {timelineObj.Observation}
                  </Typography>
                </Box>
              </Box>
            )}

            {hasDynamicForm && (
              <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
                <Chip
                  icon={<Description sx={{ fontSize: 14 }} />}
                  label={timelineObj.DynamicForm.Name}
                  size="small"
                  variant="outlined"
                  onClick={() => handleDynamicFormClick(timelineObj.DynamicForm)}
                  sx={{
                    cursor: "pointer",
                    fontSize: 11,
                    height: 26,
                    fontWeight: 600,
                    "& .MuiChip-icon": { ml: 0.5 },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </TimelineContent>
    </TimelineItem>
  );
};

const StatusChangeItemForReport = ({
  timelineObj,
  isFirstOne,
  eventModel,
  hideSignatures,
  isOriginal,
  formResultsFormatting,
  hideModificationDate,
}) => {
  const changeDate = new Date(
    isFirstOne ? eventModel?.CreationDate : timelineObj?.ChangeDate,
  ).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <div
      className="flex w-full"
      style={{
        border: "1px solid black",
        marginTop: "4px",
        borderRadius: "5px",
      }}
    >
      <div className="page-break" />
      <div
        style={{
          borderTopLeftRadius: "5px",
          borderBottomLeftRadius: "5px",
          minWidth: "20px",
          backgroundColor: isFirstOne
            ? timelineObj?.InitialStatus?.BackgroundColor
            : timelineObj?.FinalStatus?.BackgroundColor,
        }}
      />

      <div
        className="flex flex-col w-full"
        style={{ padding: "5px", width: "100%" }}
      >
        <label className="report_little_text">
          <b>{`${changeDate} - ${
            isFirstOne
              ? timelineObj?.InitialStatus?.Name
              : timelineObj?.FinalStatus?.Name
          } ${
            timelineObj.IsManual ? `(${localize.executed_manually})` : ""
          }`}</b>
        </label>

        <div className="w-full" style={{ display: "flex", gap: "10px" }}>
          <label className="report_little_text">
            <b>{`${localize.reported_by}: `}</b>
          </label>
          <label className="report_little_text">
            {isFirstOne ? eventModel?.CreatedBy : timelineObj.CreatedBy}
          </label>
        </div>

        {!isFirstOne && timelineObj?.Observation?.length > 0 && (
          <div className="w-full" style={{ display: "flex", gap: "10px" }}>
            <label className="report_little_text">
              <b>{`${localize.observations}: `}</b>
            </label>
            <label className="report_little_text">
              {timelineObj.Observation}
            </label>
          </div>
        )}
        {!isFirstOne && timelineObj?.DynamicForm && (
          <div className="w-full" style={{ marginTop: "10px" }}>
            <DynamicFormHeaderForReport
              dynamicForm={timelineObj?.DynamicForm}
              requiredForm
              eventId={eventModel?.Id}
              statusChangeId={timelineObj.Id}
              hideSignatures={hideSignatures}
              original={isOriginal}
              formResultsFormat={formResultsFormatting}
              hideModificationDate={hideModificationDate}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default function StateChangeTimeline({
  timelineData,
  firstStatusChange,
  eventModel,
  dynamicFormModel,
  setDynamicFormModel,
  isForReport,
  hideSignatures,
  isOriginal,
  canEditValues,
  formResultsFormatting,
  hideModificationDate,
}) {
  const [showModal, setShowModal] = useState(false);
  const [statusChangeId, setStatusChangeId] = useState(0);

  const totalItems = (firstStatusChange ? 1 : 0) + (timelineData?.length || 0);

  return (
    <>
      {showModal && (
        <ModalBackground
          onClickToCancel={() => {
            if (setDynamicFormModel) {
              setDynamicFormModel(getClearBasicModel());
            }
            setShowModal(false);
          }}
          onClickToSubmit={() => {}}
          hideSubmitButton
          setShowModal={setShowModal}
          showModal={showModal}
          title={dynamicFormModel?.Name}
          cancelButtonText={localize.close}
          Component={
            <DynamicFormReview
              statusChangeId={statusChangeId}
              eventId={eventModel?.Id}
              dynamicFormModel={dynamicFormModel}
              allFieldsDisabled={!canEditValues}
              hideModificationDate={hideModificationDate}
            />
          }
        />
      )}

      {!isForReport ? (
        <Timeline
          sx={{
            [`& .${timelineOppositeContentClasses.root}`]: {
              flex: { xs: "none", sm: 0 },
            },
            px: 0,
          }}
        >
          {firstStatusChange && (
            <StatusChangeTimelineItem
              key={firstStatusChange?.Id}
              timelineObj={firstStatusChange}
              isFirstOne
              eventModel={eventModel}
              isLast={timelineData?.length === 0}
            />
          )}

          {timelineData.map((timelineObj, idx) => (
            <StatusChangeTimelineItem
              key={timelineObj.Id}
              setStatusChangeId={setStatusChangeId}
              timelineObj={timelineObj}
              setDynamicFormModel={setDynamicFormModel}
              setShowDynamicFormModal={setShowModal}
              isForReport={isForReport}
              eventModel={eventModel}
              isLast={idx === timelineData.length - 1}
            />
          ))}
        </Timeline>
      ) : (
        <div className="w-full">
          {firstStatusChange && (
            <>
              <div className="page-break" />
              <StatusChangeItemForReport
                isFirstOne
                timelineObj={firstStatusChange}
                eventModel={eventModel}
                hideSignatures={hideSignatures}
                isOriginal={isOriginal}
                formResultsFormatting={formResultsFormatting}
                hideModificationDate={hideModificationDate}
              />
            </>
          )}

          {timelineData.map((timelineObj) => (
            <div key={timelineObj.Id}>
              <div className="page-break" />
              <StatusChangeItemForReport
                timelineObj={timelineObj}
                eventModel={eventModel}
                hideSignatures={hideSignatures}
                isOriginal={isOriginal}
                formResultsFormatting={formResultsFormatting}
                hideModificationDate={hideModificationDate}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
